import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { NAV_LINKS } from "../lib/data";
import { useCart } from "../lib/CartContext";

const isExternal = (href) => href.startsWith("/") && !href.startsWith("/#");

export const Navbar = ({ onCtaClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { totalItems, open: openCart } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Always show solid nav on sub-pages
  const onSubPage = location.pathname !== "/";
  const showSolid = scrolled || onSubPage;

  const renderLink = (l, onClick) => {
    const className =
      "text-[11px] uppercase tracking-[0.28em] text-neutral-400 hover:text-amber-400 transition-colors";
    if (isExternal(l.href)) {
      return (
        <Link
          key={l.href}
          to={l.href}
          onClick={onClick}
          data-testid={`nav-link-${l.label.toLowerCase()}`}
          className={className}
        >
          {l.label}
        </Link>
      );
    }
    return (
      <a
        key={l.href}
        href={l.href}
        onClick={onClick}
        data-testid={`nav-link-${l.label.toLowerCase()}`}
        className={className}
      >
        {l.label}
      </a>
    );
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 border-b ${
        showSolid
          ? "bg-black/85 backdrop-blur-md border-neutral-800"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3"
        >
          <img
            src="/brand/logo.png"
            alt="AfroAm Basketball Association"
            className="h-10 md:h-12 w-auto object-contain"
          />
          <span className="hidden sm:inline font-display text-lg md:text-xl tracking-tight text-white leading-none">
            AFROAM<span className="text-amber-400">.</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => renderLink(l))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <button
            onClick={openCart}
            data-testid="nav-cart"
            aria-label="Open cart"
            className="relative text-neutral-300 hover:text-amber-400 transition-colors p-2"
          >
            <ShoppingBag size={18} />
            {totalItems > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-1 -right-1 bg-amber-400 text-black text-[10px] font-bold leading-none px-1.5 py-0.5 rounded-full min-w-[18px] text-center"
              >
                {totalItems}
              </span>
            )}
          </button>
          {onSubPage ? (
            <Link
              to="/#contact"
              data-testid="nav-cta-join"
              className="bg-amber-400 text-black px-5 py-3 text-[11px] uppercase tracking-[0.25em] font-bold hover:bg-amber-300 transition-colors"
            >
              Join
            </Link>
          ) : (
            <button
              onClick={onCtaClick}
              data-testid="nav-cta-join"
              className="bg-amber-400 text-black px-5 py-3 text-[11px] uppercase tracking-[0.25em] font-bold hover:bg-amber-300 transition-colors"
            >
              Join
            </button>
          )}
        </div>

        <div className="lg:hidden flex items-center gap-1">
          <button
            onClick={openCart}
            data-testid="mobile-cart"
            aria-label="Open cart"
            className="relative text-neutral-300 hover:text-amber-400 transition-colors p-2"
          >
            <ShoppingBag size={20} />
            {totalItems > 0 && (
              <span className="absolute top-0 right-0 bg-amber-400 text-black text-[10px] font-bold leading-none px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                {totalItems}
              </span>
            )}
          </button>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            className="text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-black border-t border-neutral-800"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => renderLink(l, () => setOpen(false)))}
            <Link
              to="/#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-join"
              className="bg-amber-400 text-black px-5 py-3 text-xs uppercase tracking-[0.25em] font-bold mt-2 text-center"
            >
              Join the Movement
            </Link>
          </div>
        </div>
      )}
    </motion.header>
  );
};
